import React, { useCallback, useState, useEffect } from 'react'
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from './SecondCarouselArrowButtons'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import styled from 'styled-components'

const Large = styled.div`
  @media (max-width: 991px) {
    display: none;
  }
`

const Small = styled.div`
  display: none;
  @media (max-width: 991px) {
    display: flex;
    justify-content: center;
  }
`

const StyledCarousel = styled.div`
  display: flex;
  @media (max-width: 991px) {
    display: block;
  }
`

const CarouselTitle = styled.b`
  font-size: 48px;
  text-align: center;
  color: var(--bs-primary);
  @media (max-width: 991px) {
    margin-top: 60px;
  }
  @media (max-width: 575px) {
    font-size: 40px;
  }
  @media (max-width: 410px) {
    font-size: 36px;
  }
`

const BgImage = styled.div`
  width: 100%;
  height: 100%;
  padding: 15px 35px 50px 40px;
  border-radius: 10px;
  background-repeat: no-repeat;
  background-color: white;
  background-size: 100% 355px;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0) 40%,
      rgba(255, 255, 255, 1)
    ),
    url(${(props) => props.image});
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media (max-width: 575px) {
    background-size: 100% 280px;
  }
  @media (max-width: 380px) {
    background-size: 100% 200px;
  }
`

const BgIndigo = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 10px;
  background-repeat: no-repeat;
  background-color: white;
  background-size: 100% 350px;
  background-image: ${(props) =>
    props.image
      ? `linear-gradient(rgba(2, 33, 82, 0.35) 40%, rgba(255, 255, 255, 1)), url(${props.image})`
      : `linear-gradient(rgba(2, 33, 82, 0.35) 40%, rgba(255, 255, 255, 1))`};
  @media (max-width: 575px) {
    background-size: 100% 280px;
  }
  @media (max-width: 380px) {
    background-size: 100% 200px;
  }
`

const Table = styled.table`
  color: #000000;
  tr {
    height: 40px;
  }
  tr,
  th {
    font-weight: 300;
    font-size: 14px;
    text-align: start;
    width: 80px;
    min-width: 80px;
    &::after {
      content: '';
    }
    @media (max-width: 385px) {
      font-size: 12px;
    }
  }
  tr,
  td {
    font-weight: 500;
    font-size: 20px;
    text-align: end;
    @media (max-width: 575px) {
      font-size: 18px;
    }
    @media (max-width: 385px) {
      font-size: 16px;
    }
    @media (max-width: 370px) {
      font-size: 14px;
    }
    @media (max-width: 370px) {
      font-size: 14px;
    }
    @media (max-width: 350px) {
      font-size: 13px;
    }
  }
`

const Number = styled.div`
  color: var(--bs-primary);
  opacity: 0.4;
  font-size: 68px;
  font-weight: bold;
  margin-top: ${(props) => (props.value === 'string' ? '10px' : '0px')};
  font-size: ${(props) => (props.value === 'string' ? '48px' : '68px')};
  @media (max-width: 410px) {
    font-size: ${(props) => (props.value === 'string' ? '42px' : '58px')};
  }
`

const Text = styled.div`
  color: white;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  @media (max-width: 575px) {
    font-size: 18px;
  }
  @media (max-width: 380px) {
    font-size: 16px;
  }
  @media (max-width: 350px) {
    font-size: 15px;
  }
  span {
    font-size: 25px;
    font-weight: 700;
    @media (max-width: 575px) {
      font-size: 23px;
    }
    @media (max-width: 380px) {
      font-size: 19px;
    }
  }
`

const EmblaCarousel = (props) => {
  const { options } = props
  const autoplayOptions = { delay: 3000, stopOnInteraction: false }
  const [secondRef, secondApi] = useEmblaCarousel(options, [
    Autoplay(autoplayOptions),
  ])
  const [currentIndex, setCurrentIndex] = useState(0)

  const onNavButtonClick = useCallback((secondApi) => {
    const autoplay = secondApi?.plugins()?.autoplay
    if (!autoplay) return

    const resetOrStop =
      autoplay.options.stopOnInteraction === false
        ? autoplay.reset
        : autoplay.stop

    resetOrStop()
  }, [])

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(secondApi, onNavButtonClick)

  useEffect(() => {
    if (!secondApi) return

    const handleSelect = () => {
      setCurrentIndex(secondApi.selectedScrollSnap())
    }

    secondApi.on('select', handleSelect)
    return () => secondApi.off('select', handleSelect)
  }, [secondApi])

  const calculateLeftPosition = () => {
    switch (currentIndex) {
      case 0:
        return '-0.1%'
      case 1:
        return '20%'
      case 2:
        return '40%'
      case 3:
        return '60%'
      case 4:
        return '80%'
      default:
        return '-0.1%'
    }
  }

  return (
    <StyledCarousel>
      <div
        className="bg-skyblue"
        style={{
          padding: '0 5rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CarouselTitle>창고 현황</CarouselTitle>
        <Large className="carousel__controls">
          <div className="position-relative my-auto">
            <img
              src="/white-line2.svg"
              alt="white line"
              id="carousel-white-line"
              width={175}
            />
            <img
              src="/indigo-line2.svg"
              alt="indigo line"
              id="carousel-indigo-line"
              style={{
                position: 'absolute',
                left: calculateLeftPosition(),
                top: '10px',
              }}
            />
          </div>
          <div className="carousel__buttons">
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </div>
        </Large>
      </div>
      <section className="second__carousel">
        <div className="carousel__viewport" ref={secondRef}>
          <div className="carousel__container">
            <div className="carousel__slide">
              <div className="carousel__slide__content">
                <BgImage image="/distribution/carousel-1.jpg">
                  <Number>1 2 3 4</Number>
                  <Table>
                    <tr>
                      <th>건물</th>
                      <td>750평×4</td>
                    </tr>
                    <tr>
                      <th>야드</th>
                      <td>16,500m²(5,000평)</td>
                    </tr>
                    <tr>
                      <th>저장 능력(톤)</th>
                      <td>각 750</td>
                    </tr>
                    <tr>
                      <th>면적(평)</th>
                      <td>각 10,000</td>
                    </tr>
                  </Table>
                </BgImage>
              </div>
            </div>
            <div className="carousel__slide">
              <div className="carousel__slide__content">
                <BgImage image="/distribution/carousel-2.jpg">
                  <Number>5</Number>
                  <Table>
                    <tr>
                      <th>건물</th>
                      <td>1,450평</td>
                    </tr>
                    <tr>
                      <th>야드</th>
                      <td>29,700m²(9,000평)</td>
                    </tr>
                    <tr>
                      <th>저장 능력(톤)</th>
                      <td>20,000</td>
                    </tr>
                    <tr>
                      <th>면적(평)</th>
                      <td>각 1,450</td>
                    </tr>
                  </Table>
                </BgImage>
              </div>
            </div>
            <div className="carousel__slide">
              <div className="carousel__slide__content">
                <BgImage image="/distribution/carousel-3.jpg">
                  <Number>6</Number>
                  <Table>
                    <tr>
                      <th>건물</th>
                      <td>1,450평</td>
                    </tr>
                    <tr>
                      <th>야드</th>
                      <td>29,700m²(9,000평)</td>
                    </tr>
                    <tr>
                      <th>저장 능력(톤)</th>
                      <td>20,000</td>
                    </tr>
                    <tr>
                      <th>면적(평)</th>
                      <td>각 1,450</td>
                    </tr>
                  </Table>
                </BgImage>
              </div>
            </div>
            <div className="carousel__slide">
              <div className="carousel__slide__content">
                <BgIndigo image="/distribution/carousel-1.jpg">
                  <div
                    style={{
                      backdropFilter: 'blur(5px)',
                      height: '100%',
                      borderRadius: '10px',
                      padding: '15px 35px 50px 40px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Number>7</Number>
                    <Text>
                      * 고객의 맞춤형 창고로 건설 예정
                      <br />
                      <span>(2025년 03월 완공 예정)</span>
                    </Text>
                    <Table>
                      <tr>
                        <th>면적(평)</th>
                        <td>19,000m²(5,800평)</td>
                      </tr>
                      <tr>
                        <th></th>
                        <td></td>
                      </tr>
                      <tr>
                        <th></th>
                        <td></td>
                      </tr>
                      <tr>
                        <th></th>
                        <td></td>
                      </tr>
                    </Table>
                  </div>
                </BgIndigo>
              </div>
            </div>
            <div className="carousel__slide">
              <div className="carousel__slide__content">
                <BgIndigo>
                  <div
                    style={{
                      backdropFilter: 'blur(5px)',
                      height: '100%',
                      borderRadius: '10px',
                      padding: '15px 35px 50px 40px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Number value="string">포장야드</Number>
                    <Table>
                      <tr>
                        <th>면적(평)</th>
                        <td>14,000평</td>
                      </tr>
                      <tr>
                        <th>야드</th>
                        <td>50,000톤</td>
                      </tr>
                      <tr>
                        <th>저장 능력(톤)</th>
                        <td>30ft, 40ft</td>
                      </tr>
                      <tr>
                        <th></th>
                        <td></td>
                      </tr>
                    </Table>
                  </div>
                </BgIndigo>
              </div>
            </div>
          </div>
          <Small className="carousel__controls">
            <div className="position-relative my-auto">
              <img
                src="/white-line2.svg"
                alt="white line"
                id="carousel-white-line"
                width={175}
              />
              <img
                src="/indigo-line2.svg"
                alt="indigo line"
                id="carousel-indigo-line"
                style={{
                  position: 'absolute',
                  left: calculateLeftPosition(),
                  top: '10px',
                }}
              />
            </div>
            <div className="carousel__buttons">
              <PrevButton
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
              />
              <NextButton
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
              />
            </div>
          </Small>
        </div>
      </section>
    </StyledCarousel>
  )
}

export default EmblaCarousel
